import { VerifiedUser } from "@mui/icons-material";
import { Box, Button, Checkbox, Container, FormControl, FormControlLabel, FormLabel, Grid, Paper, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/pt-br';
import { ChangeEvent, FC, useState } from "react";
import InputMask from 'react-input-mask';
import { useAlert } from "../../components/contextProviders/AlertProvider";
import PrivacyPolicyLink from "../../components/privacy-policy-link/PrivacyPolicyLink";
import logo from "../../images/previni.jpeg";
import { AudiometryService } from "../../services/AudiometryService";
import { AudiometryDTO } from "../../types/AudiometryDTO";
import { useUtils } from "../../utils/utilFunctions";
import { LGPDTerm } from "../audit-admission-form/AuditAdmissionForm";
import "./AudiometryForm.scss";

const AudiometryForm: FC = () => {
	const [audiometry, setAudiometry] = useState<AudiometryDTO>({} as AudiometryDTO);
	const { validateCNPJ, validateCPF } = useUtils();
	const { showError, showAlert } = useAlert();
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [loading, setLoading] = useState(false);
	const [consent, setConsent] = useState(false);
	const [consent2, setConsent2] = useState(false);
	const [consent3, setConsent3] = useState(false);
	const service = new AudiometryService();

	const invalidCpf = !!audiometry.cpf && !audiometry.cpf.includes("_") && !validateCPF(audiometry.cpf);

	const handleChange = (field: keyof AudiometryDTO, value: any) => {
		setAudiometry(prev => ({ ...prev, [field]: value }));
	};

	const attendanceType = [
		"Audiometria admissional",
		"Audiometria periódica",
		"Audiometria mudança de função",
		"Audiometria retorno ao trabalho",
		"Audiometria demissional",
		"Audiometria avulsa"
	];

	const handleValidateCnpj = async () => {
		setLoading(true);
		try {
			const response = await service.validateCnpj(audiometry.cnpj);
			if (response && response.data) {
				setAudiometry(response.data);
				showAlert("CNPJ validado com sucesso!");
			} else {
				showError("CNPJ não encontrado ou inválido.");
			}
		} catch (error) {
			showError("CNPJ não encontrado ou inválido.");
		} finally {
			setLoading(false);
		}
	};

	const isFormValid = () => {
		return !!audiometry.employeeName
		&& !!audiometry.birthDate
		&& !!audiometry.cpf
		&& !!audiometry.sex
		&& !!audiometry.phone
		&& !!audiometry.attendanceType
		&& !!audiometry.email
		&& consent
		&& consent2
		&& consent3
		&& !audiometry.cpf.includes("_")
		&& !invalidCpf;
	};

	const handleRegister = async () => {
		if (!isFormValid()) {
			showError("Por favor, preencha todos os campos e aceite o termo de consentimento.");
			return;
		}
		setLoading(true);
		try {
			await service.register(audiometry);
			showAlert("Cadastro realizado com sucesso!");
			setIsSubmitted(true);
		} catch (error) {
			showError("Ocorreu um erro ao realizar o cadastro.");
		} finally {
			setLoading(false);
		}
	};

	if (isSubmitted) {
		return (
			<div className="audiometry-form-page">
				<Container component={Paper} className="form-container">
					<Box className="conclusion-view">
						<Typography variant="h5" gutterBottom>Obrigado!</Typography>
						<Typography variant="body1">Seus dados foram enviados com sucesso.</Typography>
					</Box>
				</Container>
			</div>
		);
	}

	return (
		<div className="audiometry-form-page">
			<Container component={Paper} className="form-container">
				<Box className="form-header">
					<img src={logo} alt="Logo da Clínica" className="logo" />
					<Box className="clinic-info">
						<Typography variant="subtitle1"><strong>Clínica Responsável</strong></Typography>
						<Typography variant="h6">Clínica Previni Medicina Ocupacional</Typography>
						<Typography variant="caption">Rua 28 de setembro, 221 - Sala 202 - Santa Cruz do Sul - RS</Typography>
						<Typography variant="caption">Fone: (51) 99931-7048 / (51) 3717-4171</Typography>
					</Box>
				</Box>
				<Typography variant="h5" component="h1" className="form-title">
					Cadastro para audiometria
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Box className="cnpj-section">
							<InputMask
								mask="99.999.999/9999-99"
								value={audiometry.cnpj}
								onChange={(e) => handleChange("cnpj", e.target.value)}
								disabled={!!audiometry.status}
							>
								{(inputProps: any) => <TextField size="small" {...inputProps} label="CNPJ" variant="outlined" fullWidth />}
							</InputMask>
							<Button
								variant="contained"
								onClick={handleValidateCnpj}
								disabled={!validateCNPJ(audiometry.cnpj) || !!audiometry.status || loading}
							>
								{loading && !!audiometry.status ? "Validando..." : "Validar"}
							</Button>
						</Box>
					</Grid>
					{!!audiometry.status && (
						<>
							<Grid item xs={12} className="company-data">
								<Typography variant="h6" className="section-title"><strong>Dados da empresa</strong></Typography>
								<Grid container spacing={2} className="company-info-grid">
									<Grid item xs={12} sm={6}>
										<Typography><strong>Razão Social:</strong></Typography>
										<Typography variant="body2" component="p">{audiometry.companyName}</Typography>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Typography><strong>Nome Fantasia:</strong></Typography>
										<Typography variant="body2" component="p">{audiometry.tradingName || "---"}</Typography>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Typography><strong>Situação:</strong></Typography>
										<Typography variant="body2" component="p">{audiometry.status}</Typography>
									</Grid>
									<Grid item xs={12} sm={6}>
										<Typography><strong>CNAE:</strong></Typography>
										<Typography variant="body2" component="p">{audiometry.cnae}</Typography>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={12} className="attendance-type-section">
								<FormControl component="fieldset" fullWidth>
									<Typography variant="h6" className="section-title"><strong>Tipo de atendimento</strong></Typography>
									<Typography variant="caption" display="block" sx={{ mb: 1 }}>
										Selecione o tipo de exame que será realizado para este funcionário
									</Typography>
									<RadioGroup
										name="attendanceType"
										value={audiometry.attendanceType}
										onChange={(e) => handleChange("attendanceType", e.target.value)}
									>
										{attendanceType.map(type => (
											<FormControlLabel key={type} value={type} control={<Radio />} label={type} />
										))}
									</RadioGroup>
								</FormControl>
							</Grid>
							<Grid item xs={12} className="employee-data">
								<Typography variant="h6" className="section-title"><strong>Dados do funcionário</strong></Typography>
								<Grid container spacing={3}>
									<Grid item xs={12}>
										<TextField fullWidth label="Nome completo" variant="outlined" value={audiometry.employeeName} onChange={(e) => handleChange("employeeName", e.target.value)} />
									</Grid>
									<Grid item xs={12} sm={6}>
										<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
											<DatePicker
												label="Data de nascimento"
												sx={{ width: '100%' }}
												value={audiometry.birthDate}
												onChange={(v) => {
													if (typeof v === 'object' && v !== null)
													handleChange("birthDate", v.format('YYYY-MM-DD'));
												}}
											/>
										</LocalizationProvider>
									</Grid>
									<Grid item xs={12} sm={6}>
										<InputMask mask="999.999.999-99"
											value={audiometry.cpf}
											onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange("cpf", e.target.value)}
											>
											{(inputProps: any) => (
												<TextField
													error={invalidCpf}
													helperText={invalidCpf ? "CPF inválido" : ""}
													{...inputProps}
													label={"CPF"}
													variant="outlined"
													fullWidth
												/>
											)}
										</InputMask>
									</Grid>
									<Grid item xs={12} sm={6}>
										<FormControl component="fieldset">
											<FormLabel component="legend">Sexo</FormLabel>
											<RadioGroup row name="sexo" value={audiometry.sex} onChange={(e) => handleChange("sex", e.target.value)}>
												<FormControlLabel value="F" control={<Radio />} label="Feminino" />
												<FormControlLabel value="M" control={<Radio />} label="Masculino" />
											</RadioGroup>
										</FormControl>
									</Grid>
									<Grid item xs={12} sm={6}>
										<InputMask mask="(99) 99999-9999" value={audiometry.phone} onChange={(e: any) => handleChange("phone", e.target.value)}>
											{(inputProps: any) => <TextField {...inputProps} label="Telefone (WhatsApp)" variant="outlined" fullWidth />}
										</InputMask>
									</Grid>
									<Grid item xs={12}>
										<TextField fullWidth label="E-mail pessoal" type="email" variant="outlined" value={audiometry.email} onChange={(e) => handleChange("email", e.target.value)} />
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={12}>
								<Paper elevation={3} className="lgpd-consent">
									{LGPDTerm}
									<Box sx={{ mt: 1 }}>
										<FormControlLabel
											control={
												<Checkbox
													checked={consent}
													onChange={(e) => setConsent(e.target.checked)}
													size="small"
												/>
											}
											label="Li e concordo com o termo de consentimento"
										/>
										<FormControlLabel
											control={
												<Checkbox
													checked={consent2}
													onChange={(e) => setConsent2(e.target.checked)}
													size="small"
												/>
											}
											label="Autrizo o tratamento dos meus dados para realização de exames ocupacionais"
										/>
										<FormControlLabel
											control={
												<Checkbox
													checked={consent3}
													onChange={(e) => setConsent3(e.target.checked)}
													size="small"
												/>
											}
											label={<>Estou ciente da <PrivacyPolicyLink label="política de privacidade" /></>}
										/>
									</Box>
								</Paper>
								<Paper elevation={2} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
									<VerifiedUser color="primary" />
									<Typography variant="caption">
										Seus dados serão usados exclusivamente para finalidades relacionadas à saúde ocupacional e serão tratados de acordo com nossa <PrivacyPolicyLink />
									</Typography>
								</Paper>
							</Grid>
							<Grid item xs={12} className="form-actions">
								<Button variant="contained" color="success" onClick={handleRegister} disabled={!isFormValid() || loading}>
									{loading ? "Cadastrando..." : "Cadastrar"}
								</Button>
							</Grid>
						</>
					)}
				</Grid>
			</Container>
		</div>
	);
}

export default AudiometryForm;